import { DateTime } from 'luxon';
import { IListing } from '../interfaces/listing';
import { IReservation } from '../interfaces/reservation';
import { INudgeHost, NudgeReason } from '../interfaces/response/admin-analytics';
import { IUser } from '../interfaces/user';
import { bookedAt, bookingGroup, median, ratio, requestAnswer } from './booking-analytics';
import { liveHosts } from './host-analytics';

/**
 * "Hosts to nudge": hosts with a request waiting, who reply slowly, who decline most requests, or
 * who are live with no recent bookings. Shared by the server (`GET admin/reservation/analytics/nudge`)
 * and its tests; the admin keeps the labels and the ready-made emails.
 */

/** A request unanswered this long is overdue (the same day the guest site promises). */
export const WAITING_HOURS = 24;
/** The look-back for reply times, declines and bookings. */
export const NUDGE_DAYS = 90;
/** Too few answers say nothing about a host: at least this many before "slow" or "declines a lot". */
export const MIN_ANSWERED_SLOW = 2;
export const MIN_ANSWERED_DECLINES = 3;
export const DECLINE_SHARE = 0.5;

const RANK: Record<NudgeReason, number> = { waiting: 0, slow: 1, declines: 2, quiet: 3 };

const hostOf = (r: IReservation): IUser | undefined => r.site?.listing?.user;
const nameOf = (u: IUser | undefined, id: number) =>
  `${u?.firstName ?? ''} ${u?.lastName ?? ''}`.trim() || `Host #${id}`;
const statusOf = (r: IReservation) =>
  String(r.status || r.activity?.[r.activity.length - 1]?.status || '').toLowerCase();

/**
 * Hosts worth a call or an email, each with why:
 * - waiting: a booking request still unanswered after WAITING_HOURS, stay not yet started;
 * - slow: median reply over WAITING_HOURS, on MIN_ANSWERED_SLOW+ requests in the look-back;
 * - declines: under half accepted, on MIN_ANSWERED_DECLINES+ requests in the look-back;
 * - quiet: live the whole look-back with no booking made in it.
 * Most urgent first: waiting, then slow, declines, quiet.
 *
 * `reservations` needs every request still waiting and every reservation made in the look-back;
 * `listings` every published listing, with its owner's contact details. `lastBookings` is each
 * host's newest booking ever, when the reservations don't reach back that far.
 */
export function hostsToNudge(
  reservations: IReservation[],
  listings: IListing[],
  now: DateTime = DateTime.now(),
  lastBookings: Map<number, DateTime> = new Map(),
): INudgeHost[] {
  const since = now.minus({ days: NUDGE_DAYS });
  const today = now.toISODate() as string;
  const liveNow = liveHosts(listings, now);
  const liveThroughout = liveHosts(listings, since);

  type Draft = Omit<INudgeHost, 'lastBooking'> & {
    lastBooking?: DateTime;
    replies: number[];
    accepted: number;
    booked: boolean;
  };
  const hosts = new Map<number, Draft>();
  const host = (id: number, user?: IUser) => {
    let h = hosts.get(id);
    if (!h) {
      h = {
        id,
        name: nameOf(user, id),
        email: user?.email || undefined,
        phone: user?.phone || undefined,
        reasons: [],
        waiting: { count: 0, oldestHours: 0 },
        answered: 0,
        acceptance: null,
        medianReplyHours: null,
        listings: [],
        replies: [],
        accepted: 0,
        booked: false,
      };
      hosts.set(id, h);
    }
    // Listing owners carry contact details; a reservation's nested host may not.
    if (user) {
      h.email ??= user.email || undefined;
      h.phone ??= user.phone || undefined;
      if (h.name.startsWith('Host #')) h.name = nameOf(user, id);
    }
    return h;
  };

  for (const l of listings) {
    const id = l.user?.id;
    if (!id || !liveNow.has(id) || l.deleted || !l.available) continue;
    host(id, l.user).listings.push({ id: l.id as number, name: l.name?.trim() || `Listing #${l.id}` });
  }

  for (const r of reservations) {
    const owner = hostOf(r);
    if (!owner?.id) continue;
    const made = bookedAt(r);
    if (statusOf(r) === 'requested' && made) {
      const hours = now.diff(made, 'hours').hours;
      const start = String(r.date?.from ?? '').slice(0, 10);
      if (hours > WAITING_HOURS && (!start || start >= today)) {
        const h = host(owner.id, owner);
        h.waiting.count += 1;
        if (hours > h.waiting.oldestHours) {
          h.waiting.oldestHours = Math.round(hours);
          h.waiting.reservationId = r.id;
        }
      }
    }
    const isBooked = bookingGroup(r) === 'booked';
    if (isBooked && made) {
      const h = host(owner.id, owner);
      if (!h.lastBooking || made > h.lastBooking) h.lastBooking = made;
      if (made >= since) h.booked = true;
    }
    if (!made || made < since) continue;
    const answer = requestAnswer(r);
    if (answer) {
      const h = host(owner.id, owner);
      h.answered += 1;
      if (answer.accepted) h.accepted += 1;
      h.replies.push(answer.hours);
    }
  }

  const out: INudgeHost[] = [];
  for (const { replies, accepted, booked, lastBooking, ...h } of hosts.values()) {
    h.acceptance = ratio(accepted, h.answered);
    h.medianReplyHours = median(replies);
    if (h.waiting.count) h.reasons.push('waiting');
    if (h.answered >= MIN_ANSWERED_SLOW && (h.medianReplyHours ?? 0) > WAITING_HOURS) h.reasons.push('slow');
    if (h.answered >= MIN_ANSWERED_DECLINES && (h.acceptance ?? 1) < DECLINE_SHARE) h.reasons.push('declines');
    if (liveThroughout.has(h.id) && liveNow.has(h.id) && !booked) h.reasons.push('quiet');
    if (!h.reasons.length) continue;
    const known = lastBookings.get(h.id);
    const last = known && (!lastBooking || known > lastBooking) ? known : lastBooking;
    out.push({ ...h, lastBooking: last?.toISO() ?? undefined });
  }
  return out.sort(
    (a, b) =>
      RANK[a.reasons[0]] - RANK[b.reasons[0]] ||
      b.waiting.oldestHours - a.waiting.oldestHours ||
      (b.medianReplyHours ?? 0) - (a.medianReplyHours ?? 0) ||
      a.name.localeCompare(b.name),
  );
}
